import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Logo } from "@/shell/BootScreen";
import { useAuth } from "@/auth/auth";

/**
 * Mobilní chrome (spec §74, /Design Mobile): top bar se zpět-tlačítkem na
 * detailech, in-app push banner pro kritické věci a bottom tab bar
 * PŘEHLED / ROZHODNUTÍ / ÚKOLY / JARVIS / MENU. Přes MENU je dostupná celá
 * aplikace — mobil vidí všechno co desktop, jen ve fokusovanějším chrome.
 *
 * Úkoly pro člověka mají vlastní tab, protože jsou ze čtyř schránek nejvíc
 * mobilní: „zkontroluj vzhled a diakritiku na vytištěných páskách" ownera
 * zastihne u tiskárny, ne u počítače. Blokace a návrhy jsou v MENU i s počty —
 * ty se řeší u stolu, ale schovat je před ownerem by byl krok zpátky.
 */

/** Routy, které mají vlastní tab — všude jinde se hodí zpět-tlačítko. */
const ROOT_PATHS = new Set(["/", "/decisions", "/human-tasks", "/jarvis"]);

const MENU_ITEMS: { to: string; label: string; badge?: "blockers" | "proposals" }[] = [
  { to: "/blockers", label: cs.nav.blockers, badge: "blockers" },
  { to: "/proposals", label: cs.nav.proposals, badge: "proposals" },
  { to: "/projects", label: cs.nav.projects },
  { to: "/plan", label: cs.nav.plan },
  { to: "/goals", label: cs.nav.goals },
  { to: "/workers", label: cs.nav.workers },
  { to: "/departments", label: cs.nav.departments },
  { to: "/audit", label: cs.nav.audit },
];

export function MobileShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const router = useRouter();
  const [time, setTime] = useState(() => new Date());
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 5000);
    return () => clearInterval(t);
  }, []);
  // navigace zavírá menu sheet
  useEffect(() => setMenuOpen(false), [pathname]);
  const health = useQuery({ queryKey: ["health"], queryFn: api.health, refetchInterval: 60_000, retry: false });
  const inbox = useQuery({ queryKey: ["inbox"], queryFn: () => api.decisionInbox(), refetchInterval: 30_000 });
  const humanTasks = useQuery({
    queryKey: ["human-tasks"],
    queryFn: () => api.humanTasks(),
    refetchInterval: 30_000,
  });
  const decisionCount = (inbox.data?.items ?? []).length;
  const humanTaskCount = (humanTasks.data?.items ?? []).length;

  const tab =
    pathname === "/decisions" ? "decisions"
    : pathname === "/human-tasks" ? "human-tasks"
    : pathname === "/jarvis" ? "jarvis"
    : pathname === "/" ? "home"
    : "menu";
  const showBack = !ROOT_PATHS.has(pathname);

  return (
    <div className="flex min-h-screen flex-col overflow-x-clip" data-testid="mobile-shell">
      {/* top bar */}
      <div className="flex items-center justify-between px-4 pb-2 pt-3.5">
        <div className="flex min-w-0 items-center gap-2.5">
          {showBack ? (
            <button
              type="button"
              className="-ml-1 cursor-pointer py-1 pr-1 text-[12px] tracking-[0.14em]"
              style={{ color: "var(--accent)" }}
              onClick={() => router.history.back()}
              data-testid="mobile-back"
            >
              {cs.mobile.back}
            </button>
          ) : null}
          <Link to="/" className="flex min-w-0 items-center gap-2.5" style={{ color: "inherit" }}>
            <Logo />
            <span className="af-display truncate text-[11px] font-bold tracking-[0.22em]" style={{ color: "var(--text-primary)" }}>
              {cs.app.name}
            </span>
          </Link>
        </div>
        <div className="flex flex-none items-center gap-2.5 text-[11px]">
          <span style={{ color: health.data?.status === "OPERATIONAL" ? "var(--positive)" : "var(--critical)" }}>
            {cs.topbar.statusOperational}
          </span>
          <span style={{ color: "var(--text-tertiary)" }}>
            {time.toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
      </div>

      <MobilePushBanner />

      <main className="min-w-0 flex-1 px-3.5 pb-28 pt-2">{children}</main>

      {menuOpen ? <MobileMenuSheet onClose={() => setMenuOpen(false)} /> : null}

      {/* tab bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 grid grid-cols-5"
        style={{
          borderTop: "1px solid color-mix(in srgb, var(--accent) 15%, transparent)",
          background: "var(--surface-overlay)",
          backdropFilter: "blur(4px)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
        data-testid="mobile-tabbar"
      >
        <MobileTab to="/" active={tab === "home"} icon="◈" label={cs.mobile.tabs.home} />
        <MobileTab
          to="/decisions"
          active={tab === "decisions"}
          icon="▣"
          label={cs.mobile.tabs.decisions}
          badge={decisionCount}
        />
        <MobileTab
          to="/human-tasks"
          active={tab === "human-tasks"}
          icon="✋"
          label={cs.mobile.tabs.humanTasks}
          badge={humanTaskCount}
        />
        <MobileTab to="/jarvis" active={tab === "jarvis"} icon="orb" label={cs.mobile.tabs.jarvis} />
        <button
          type="button"
          className="relative cursor-pointer pb-4 pt-3 text-center"
          style={{
            borderTop: `2px solid ${tab === "menu" || menuOpen ? "var(--accent)" : "transparent"}`,
            color: tab === "menu" || menuOpen ? "var(--accent)" : "var(--text-tertiary)",
          }}
          onClick={() => setMenuOpen((v) => !v)}
          data-testid="mobile-tab-MENU"
        >
          <div className="text-[14px]">☰</div>
          <div className="mt-0.5 text-[10px] tracking-[0.06em]">{cs.mobile.tabs.menu}</div>
        </button>
      </nav>
    </div>
  );
}

function MobileTab({
  to,
  active,
  icon,
  label,
  badge,
}: {
  to: string;
  active: boolean;
  icon: string;
  label: string;
  badge?: number;
}) {
  const color = active ? "var(--accent)" : "var(--text-tertiary)";
  return (
    <Link
      to={to}
      className="relative pb-4 pt-3 text-center"
      style={{ borderTop: `2px solid ${active ? "var(--accent)" : "transparent"}`, color }}
      data-testid={`mobile-tab-${label}`}
    >
      {icon === "orb" ? (
        <div
          className="mx-auto h-4 w-4 rounded-full"
          style={{
            background: "radial-gradient(circle at 35% 30%, #8ff2ff, #0e7f96 60%, #053540)",
            boxShadow: "0 0 10px color-mix(in srgb, var(--accent) 50%, transparent)",
          }}
        />
      ) : (
        <div className="text-[14px]">{icon}</div>
      )}
      {/* v pěti sloupcích se popisek na 375px vejde jen užší — 11px/0.14em přetékalo */}
      <div className="mt-0.5 text-[10px] tracking-[0.06em]">{label}</div>
      {badge && badge > 0 ? (
        <div
          className="absolute top-2 flex h-[15px] min-w-[15px] items-center justify-center rounded-full px-1 text-[11px] font-bold"
          style={{ left: "calc(50% + 12px)", background: "var(--critical)", color: "var(--text-on-accent)" }}
          data-testid="mobile-badge"
        >
          {badge}
        </div>
      ) : null}
    </Link>
  );
}

/** Full-screen navigační sheet — zpřístupňuje na mobilu všechny sekce aplikace. */
function MobileMenuSheet({ onClose }: { onClose: () => void }) {
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Blokace a návrhy nemají vlastní tab, ale počet u nich mít musí — bez něj
  // by se v menu ztratily úplně stejně, jako se ztrácely v jedné schránce.
  const blockers = useQuery({
    queryKey: ["blockers", false],
    queryFn: () => api.blockers({ limit: 100 }),
  });
  const proposals = useQuery({ queryKey: ["proposals-inbox", false], queryFn: () => api.proposalInbox() });
  const counts: Record<string, number> = {
    blockers: blockers.data?.blockers.filter((b) => !b.resolvedAt).length ?? 0,
    proposals: proposals.data?.items.length ?? 0,
  };

  return (
    <div
      className="af-animate-fade fixed inset-0 z-40 flex flex-col overflow-y-auto px-5 pb-32 pt-6"
      style={{ background: "var(--surface-overlay)", backdropFilter: "blur(6px)" }}
      data-testid="mobile-menu-sheet"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="af-label">{cs.mobile.menuTitle}</div>
        <button type="button" className="af-label-dim cursor-pointer py-1" onClick={onClose}>
          ✕ {cs.mobile.menuClose}
        </button>
      </div>
      <div className="flex flex-col">
        {MENU_ITEMS.map((item) => {
          const active = pathname.startsWith(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              className="af-display flex items-center justify-between py-3.5 text-[13px] tracking-[0.18em]"
              style={{
                color: active ? "var(--accent)" : "var(--text-secondary)",
                borderBottom: "1px solid color-mix(in srgb, var(--accent) 8%, transparent)",
              }}
              data-testid={`mobile-menu-${item.label}`}
            >
              <span className="flex items-center gap-2">
                {item.label}
                {item.badge && counts[item.badge] ? (
                  <span
                    className="flex h-[17px] min-w-[17px] items-center justify-center rounded-full px-1 text-[11px] font-bold"
                    style={{ background: "var(--critical)", color: "var(--text-on-accent)" }}
                    data-testid={`mobile-menu-badge-${item.badge}`}
                  >
                    {counts[item.badge]}
                  </span>
                ) : null}
              </span>
              <span style={{ color: "var(--text-tertiary)" }}>→</span>
            </Link>
          );
        })}
        {session?.user.role === "OWNER" ? (
          <Link
            to="/settings"
            className="af-display flex items-center justify-between py-3.5 text-[13px] tracking-[0.18em]"
            style={{
              color: pathname.startsWith("/settings") ? "var(--accent)" : "var(--text-secondary)",
              borderBottom: "1px solid color-mix(in srgb, var(--accent) 8%, transparent)",
            }}
            data-testid="mobile-menu-settings"
          >
            <span>{cs.mobile.menuSettings}</span>
            <span style={{ color: "var(--text-tertiary)" }}>→</span>
          </Link>
        ) : null}
      </div>
      <div className="mt-6 flex items-center justify-between text-[11px]">
        <span style={{ color: "var(--text-tertiary)" }}>{session?.user.name?.toUpperCase()}</span>
        <button
          type="button"
          className="cursor-pointer py-2 tracking-[0.14em]"
          style={{ color: "var(--critical)" }}
          onClick={() => void logout().then(() => navigate({ to: "/login" }))}
          data-testid="mobile-logout"
        >
          {cs.auth.logout}
        </button>
      </div>
    </div>
  );
}

/** In-app banner pro kritické věci (URGENT decision / P0-P1 incident). */
function MobilePushBanner() {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<string[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem("af-push-dismissed") ?? "[]") as string[];
    } catch {
      return [];
    }
  });
  const inbox = useQuery({ queryKey: ["inbox"], queryFn: () => api.decisionInbox() });
  const humanTasks = useQuery({ queryKey: ["human-tasks"], queryFn: () => api.humanTasks() });
  const incidents = useQuery({ queryKey: ["incidents"], queryFn: api.incidents, refetchInterval: 30_000 });

  const urgentDecision = (inbox.data?.items ?? []).find(
    (i) => i.urgency === "URGENT" && !dismissed.includes(`d-${i.id}`),
  );
  // Úkol, který stojí projekt, je stejně kritický jako rozhodnutí — a na
  // mobilu bývá i rychleji odbytelný (stačí se na něco podívat).
  const urgentHumanTask = (humanTasks.data?.items ?? []).find(
    (t) => (t.urgency === "URGENT" || t.blocksProject) && !dismissed.includes(`h-${t.id}`),
  );
  const criticalIncident = (incidents.data?.incidents ?? []).find(
    (i) => (i.severity === "P0" || i.severity === "P1") && !dismissed.includes(`i-${i.id}`),
  );
  const item = urgentDecision
    ? { key: `d-${urgentDecision.id}`, label: cs.mobile.pushCritical, text: urgentDecision.title, to: "/decisions" }
    : urgentHumanTask
    ? {
        key: `h-${urgentHumanTask.id}`,
        label: cs.mobile.pushHumanTask,
        text: urgentHumanTask.title,
        to: "/human-tasks",
      }
    : criticalIncident
      ? {
          key: `i-${criticalIncident.id}`,
          label: cs.mobile.pushIncident,
          text: `${criticalIncident.label} · ${criticalIncident.title}`,
          to: `/incidents/${criticalIncident.id}`,
        }
      : null;
  if (!item) return null;

  const dismiss = () => {
    const next = [...dismissed, item.key];
    setDismissed(next);
    sessionStorage.setItem("af-push-dismissed", JSON.stringify(next));
  };

  return (
    <button
      type="button"
      className="af-animate-fade mx-3.5 mt-1 cursor-pointer px-3.5 py-2.5 text-left"
      style={{ borderRadius: "var(--radius-box)", border: "1px solid color-mix(in srgb, var(--critical) 40%, transparent)", background: "color-mix(in srgb, var(--critical) 7%, transparent)" }}
      onClick={() => void navigate({ to: item.to })}
      data-testid="mobile-push-banner"
    >
      <div className="mb-1 flex justify-between text-[11px] tracking-[0.18em]">
        <span style={{ color: "var(--critical)" }}>{item.label}</span>
        <span
          style={{ color: "var(--text-tertiary)" }}
          onClick={(e) => {
            e.stopPropagation();
            dismiss();
          }}
        >
          ✕
        </span>
      </div>
      <div className="text-[11px] leading-snug" style={{ color: "var(--text-primary)" }}>
        {item.text}
      </div>
      <div className="mt-1 text-[11px]" style={{ color: "var(--accent)" }}>
        {cs.mobile.pushOpen}
      </div>
    </button>
  );
}
